import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { BOOKING_TYPES, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, Download } from "lucide-react";
import { formatDateTime, formatDate } from "@/lib/date";
import { toast } from "sonner";
import { tx } from "@/i18n";

const sel = "h-8 rounded border border-input bg-background px-1.5 text-sm w-full";

const StaffOptions = ({ staff, me }) => (
  <>
    <option value="">{tx("Niet toegewezen")}</option>
    <option value={me.id}>{tx("Ikzelf")}</option>
    {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</option>)}
  </>
);

const CalendarMapping = ({ calendars, map, setMap, staff, me, calOn, toggleCal }) => (
  <div className="space-y-2" data-testid="import-calendar-mapping">
    {calendars.map((c) => (
      <div key={c.id} className="grid grid-cols-[24px_1fr] sm:grid-cols-[24px_1fr_220px] gap-2 items-center p-2.5 rounded-lg border border-border" data-testid={`import-cal-${c.id}`}>
        <Checkbox checked={calOn[c.id] !== false} onCheckedChange={(v) => toggleCal(c.id, !!v)} data-testid={`import-cal-include-${c.id}`} />
        <span className="text-sm truncate"><span className="font-medium">{c.summary}</span> <span className="text-muted-foreground">({c.count})</span></span>
        <select value={map[c.id] || ""} onChange={(e) => setMap((p) => ({ ...p, [c.id]: e.target.value }))} className={`${sel} col-span-2 sm:col-span-1`} data-testid={`import-cal-assignee-${c.id}`}>
          <StaffOptions staff={staff} me={me} />
        </select>
      </div>
    ))}
  </div>
);

const EventRow = ({ ev, row, set, houses, staff, me, assignee }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-[24px_1.6fr_1fr_1fr_1fr] gap-2 items-center p-2.5 rounded-lg border ${row.house_id ? "border-border" : "border-amber-300 bg-amber-50/50"}`} data-testid={`import-event-${ev.event_id}`}>
    <Checkbox checked={row.include} disabled={!row.house_id} onCheckedChange={(v) => set({ include: !!v })} data-testid={`import-event-include-${ev.event_id}`} />
    <div className="min-w-0">
      <div className="text-sm font-medium truncate" title={ev.title}>{ev.title}</div>
      <div className="text-xs text-muted-foreground truncate">{formatDateTime(ev.start)} · {ev.calendar}</div>
    </div>
    <select value={row.house_id} onChange={(e) => set({ house_id: e.target.value, include: !!e.target.value })} className={sel} data-testid={`import-event-house-${ev.event_id}`}>
      <option value="">{tx("— Kies woning —")}</option>
      {houses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
    </select>
    <select value={row.type} onChange={(e) => set({ type: e.target.value })} className={sel} data-testid={`import-event-type-${ev.event_id}`}>
      {Object.entries(BOOKING_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
    <select value={assignee} onChange={(e) => set({ assignee_id: e.target.value })} className={sel} data-testid={`import-event-assignee-${ev.event_id}`}>
      <StaffOptions staff={staff} me={me} />
    </select>
  </div>
);

const KIND_LABEL = { checkin: "Incheck", checkout: "Uitcheck", cleaning: "Schoonmaak" };
const WARN = {
  overlap: ["Overlapt met een ander verblijf", "bg-amber-100 text-amber-800"],
  exists: ["Bestaat al in de app", "bg-muted text-muted-foreground"],
  no_house: ["Woning niet herkend", "bg-amber-100 text-amber-800"],
  date_from_google: ["Datum aangepast naar Google", "bg-sky-100 text-sky-800"],
};

const StayRow = ({ st, on, toggle, setHouse, houses }) => {
  const kinds = st.stay_type === "eigen_verblijf" ? ["cleaning"] : ["checkin", "checkout", "cleaning"];
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-[24px_1.4fr_1fr_1.4fr] gap-2 items-start p-2.5 rounded-lg border ${on ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`import-stay-${st.stay_id}`}>
      <Checkbox checked={on} disabled={!st.house_id} onCheckedChange={(v) => toggle(!!v)} data-testid={`import-stay-include-${st.stay_id}`} />
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{st.guest_name || tx("Eigen verblijf")} <span className="text-muted-foreground font-normal">· {st.num_guests}p</span></div>
        <div className="text-xs text-muted-foreground">{formatDate(st.arrival)} → {formatDate(st.departure)}</div>
        {(st.party_composition || st.notes) && <div className="text-xs text-muted-foreground line-clamp-2" title={st.notes}>{[st.party_composition, st.notes].filter(Boolean).join(" · ")}</div>}
      </div>
      <select value={st.house_id} onChange={(e) => setHouse(e.target.value)} className={sel} data-testid={`import-stay-house-${st.stay_id}`}>
        <option value="">{tx("— Kies woning —")}</option>
        {houses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>
      <div className="flex flex-wrap gap-1">
        {kinds.map((k) => (
          <span key={k} className={`text-[11px] px-1.5 py-0.5 rounded ${st.links[k] ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>
            {tx(KIND_LABEL[k])}: {st.links[k] ? tx("Google") : (k === "checkin" && st.arrival < new Date().toISOString().slice(0, 10)) ? tx("nieuw (al geweest)") : tx("nieuw")}
          </span>
        ))}
        {st.warnings.map((w) => <span key={w} className={`text-[11px] px-1.5 py-0.5 rounded ${WARN[w][1]}`}>{tx(WARN[w][0])}</span>)}
      </div>
    </div>
  );
};

export const GoogleImportDialog = ({ open, onOpenChange, houses, staff, me, onDone }) => {
  const [data, setData] = useState(null);
  const [map, setMap] = useState({});
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [calOn, setCalOn] = useState({});
  const [filter, setFilter] = useState("all");
  const [stays, setStays] = useState([]);
  const toggleCal = (cid, v) => {
    setCalOn((p) => ({ ...p, [cid]: v }));
    setRows((p) => Object.fromEntries(Object.entries(p).map(([id, r]) => {
      const ev = data.events.find((e) => e.event_id === id);
      return [id, ev?.calendar_id === cid ? { ...r, include: v && !!r.house_id } : r];
    })));
  };

  useEffect(() => {
    if (!open) return;
    setData(null); setCalOn({}); setFilter("all");
    api.get("/calendar/import/preview").then(({ data: d }) => {
      setData(d);
      setStays(d.stays || []);
      setMap(Object.fromEntries(d.calendars.map((c) => [c.id, c.assignee_id || ""])));
      setRows(Object.fromEntries(d.events.map((e) => [e.event_id, { include: !!e.house_id, house_id: e.house_id, type: e.type, assignee_id: undefined }])));
    }).catch((err) => { toast.error(apiError(err)); onOpenChange(false); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const assigneeOf = (ev) => rows[ev.event_id]?.assignee_id ?? map[ev.calendar_id] ?? "";
  const linked = useMemo(() => new Set(stays.filter((x) => x.include).flatMap((x) => Object.values(x.links))), [stays]);
  const selected = useMemo(() => (data?.events || []).filter((e) => rows[e.event_id]?.house_id && (rows[e.event_id]?.include || linked.has(e.event_id))), [data, rows, linked]);
  const missing = (data?.events || []).filter((e) => !linked.has(e.event_id) && !rows[e.event_id]?.house_id).length;
  const visible = (data?.events || []).filter((e) => !linked.has(e.event_id) && calOn[e.calendar_id] !== false && (filter === "all" || (filter === "house") === !!rows[e.event_id]?.house_id));
  const setStay = (id, patch) => setStays((p) => p.map((x) => (x.stay_id === id ? { ...x, ...patch } : x)));
  const chosenStays = stays.filter((x) => x.include && x.house_id);

  const submit = async () => {
    setBusy(true);
    try {
      const items = selected.map((e) => ({ event_id: e.event_id, calendar_id: e.calendar_id, title: e.title, start: e.start, end: e.end,
        house_id: rows[e.event_id].house_id, type: rows[e.event_id].type, assignee_id: assigneeOf(e) || null, notes: e.description || "" }));
      const { data: r } = await api.post("/calendar/import", { calendar_assignees: map, items, stays: chosenStays });
      toast.success(tx("{n} afspraken en {m} verblijven geïmporteerd", { n: r.created, m: r.stays || 0 }));
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="google-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tx("Eenmalig importeren uit Google Agenda")}</DialogTitle>
          <DialogDescription>{tx("Alle toekomstige Google afspraken worden afspraken in de app en blijven gekoppeld. Na de import haalt de app niets meer op uit Google; boekingen gaan wel naar Google. Er worden geen berichten naar gasten verstuurd.")}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-12 flex justify-center" data-testid="import-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : data.events.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground" data-testid="import-empty">{tx("Geen nieuwe toekomstige afspraken gevonden.")}</p>
        ) : (
          <div className="space-y-6">
            <section>
              <h3 className="text-sm font-semibold mb-1">{tx("1. Agenda koppelen aan medewerker")}</h3>
              <p className="text-xs text-muted-foreground mb-3">{tx("Afspraken worden toegewezen aan de medewerker van hun agenda. Per afspraak kun je dit hieronder nog aanpassen.")}</p>
              <CalendarMapping calendars={data.calendars} map={map} setMap={setMap} staff={staff} me={me} calOn={calOn} toggleCal={toggleCal} />
            </section>
            {stays.length > 0 && (
              <section data-testid="import-stays-section">
                <h3 className="text-sm font-semibold mb-1">{tx("2. Verblijven uit de sheet")} <span className="text-muted-foreground font-normal">({chosenStays.length}/{stays.length})</span></h3>
                <p className="text-xs text-muted-foreground mb-3">{tx("Elk aangevinkt verblijf wordt een bevestigde boeking met gastgegevens. Groen = gekoppeld aan de afspraak uit Google (Google is leidend). Oranje = nieuw, wordt aangemaakt en naar Google gestuurd.")}</p>
                <div className="space-y-2">
                  {stays.map((st) => (
                    <StayRow key={st.stay_id} st={st} on={!!st.include && !!st.house_id} houses={houses}
                      toggle={(v) => setStay(st.stay_id, { include: v })} setHouse={(h) => setStay(st.stay_id, { house_id: h, include: !!h })} />
                  ))}
                </div>
              </section>
            )}
            <section>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                <h3 className="text-sm font-semibold">{stays.length > 0 ? tx("3. Overige afspraken") : tx("2. Controleer de afspraken")} <span className="text-muted-foreground font-normal">({visible.length})</span></h3>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${sel} w-auto`} data-testid="import-filter">
                  <option value="all">{tx("Alle afspraken")}</option>
                  <option value="house">{tx("Met woning")}</option>
                  <option value="nohouse">{tx("Zonder woning")}</option>
                </select>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Afspraken tot {n} jaar vooruit.", { n: Math.round((data.horizon_days || 730) / 365) })}</p>
              {missing > 0 && (
                <p className="text-xs text-amber-700 flex items-center gap-1.5 mb-3" data-testid="import-missing-house"><AlertTriangle className="h-3.5 w-3.5" /> {tx("{n} afspraken zonder herkende woning — kies een woning om ze mee te nemen.", { n: missing })}</p>
              )}
              <div className="space-y-2">
                {visible.map((ev) => (
                  <EventRow key={ev.event_id} ev={ev} row={rows[ev.event_id] || {}} houses={houses} staff={staff} me={me} assignee={assigneeOf(ev)}
                    set={(patch) => setRows((p) => ({ ...p, [ev.event_id]: { ...p[ev.event_id], ...patch } }))} />
                ))}
              </div>
            </section>
          </div>
        )}
        <DialogFooter className="sticky bottom-0 bg-background pt-3">
          <Button onClick={submit} disabled={busy || (!selected.length && !chosenStays.length)} className="bg-primary hover:bg-primary/90 gap-2" data-testid="import-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {chosenStays.length ? tx("{n} afspraken + {m} verblijven importeren", { n: selected.length, m: chosenStays.length }) : tx("{n} afspraken importeren", { n: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
